import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, ChevronDown, Folder, FolderOpen, FileCode2, FileJson, FileText, Settings2, Copy, Check, Workflow, Terminal, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function iconFor(path) {
  if (path.endsWith(".tf") || path.endsWith(".tfvars") || path.endsWith(".bicep")) return FileCode2;
  if (path.endsWith(".json")) return FileJson;
  if (path.endsWith(".yml") || path.endsWith(".yaml")) return Workflow;
  if (path.endsWith(".py") || path.endsWith(".sh")) return Terminal;
  if (path.endsWith(".md")) return FileText;
  return Settings2;
}

function buildTree(files) {
  const root = { dirs: {}, files: [] };
  files.forEach((f) => {
    const parts = f.path.split("/");
    let node = root;
    parts.slice(0, -1).forEach((d) => { node.dirs[d] = node.dirs[d] || { dirs: {}, files: [] }; node = node.dirs[d]; });
    node.files.push(f);
  });
  return root;
}

const ORDER = ["README.md", "versions.tf", "main.tf", "variables.tf", "outputs.tf", "terraform.tfvars", "backend.tf", "main.bicep", "main.parameters.json"];
const sortFiles = (a, b) => {
  const ia = ORDER.indexOf(a.path.split("/").pop()), ib = ORDER.indexOf(b.path.split("/").pop());
  if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  return a.path.localeCompare(b.path);
};

function flatten(node, depth, prefix, collapsed, out) {
  Object.keys(node.dirs).sort().forEach((d) => {
    const full = prefix ? `${prefix}/${d}` : d;
    out.push({ kind: "dir", name: d, full, depth, open: !collapsed.has(full) });
    if (!collapsed.has(full)) flatten(node.dirs[d], depth + 1, full, collapsed, out);
  });
  [...node.files].sort(sortFiles).forEach((f) => out.push({ kind: "file", file: f, depth }));
  return out;
}

function searchFiles(files, query) {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const hits = [];
  for (const f of files) {
    const lines = (f.content || "").split("\n");
    lines.forEach((line, i) => {
      if (line.toLowerCase().includes(q)) hits.push({ path: f.path, line: i + 1, text: line.trim() });
    });
    if (hits.length > 200) break;
  }
  return hits.slice(0, 200);
}

function Highlight({ text, query }) {
  const q = query.trim();
  if (!q) return text || " ";
  const parts = [];
  const lower = text.toLowerCase();
  const lq = q.toLowerCase();
  let i = 0;
  let idx = lower.indexOf(lq, i);
  while (idx !== -1) {
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(<mark key={idx} className="rounded-sm bg-amber-400/80 px-0.5 text-slate-950">{text.slice(idx, idx + q.length)}</mark>);
    i = idx + q.length;
    idx = lower.indexOf(lq, i);
  }
  if (i < text.length) parts.push(text.slice(i));
  return parts.length ? parts : " ";
}

function SearchResults({ hits, query, onJump, active }) {
  return (
    <div className="border-b border-slate-200 bg-white" data-testid="code-search-results">
      <p className="px-3 py-1.5 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400" data-testid="code-search-count">
        {hits.length ? `${hits.length}${hits.length === 200 ? "+" : ""} match${hits.length > 1 ? "es" : ""}` : "no match"}
      </p>
      <div className="max-h-56 overflow-auto">
        {hits.map((h) => (
          <button
            key={`${h.path}:${h.line}`}
            type="button"
            onClick={() => onJump(h)}
            data-testid={`code-search-hit-${h.path}-${h.line}`}
            title={`${h.path}:${h.line}`}
            className={cn("flex w-full flex-col px-3 py-1 text-left font-mono-tight text-[11px] hover:bg-slate-100", active?.path === h.path && active?.line === h.line && "bg-amber-50")}
          >
            <span className="truncate text-slate-500">{h.path}:{h.line}</span>
            <span className="truncate text-slate-800"><Highlight text={h.text} query={query} /></span>
          </button>
        ))}
      </div>
    </div>
  );
}

function TreeRow({ row, current, onToggle, onSelect }) {
  if (row.kind === "dir") {
    return (
      <button type="button" onClick={() => onToggle(row.full)} data-testid={`dir-${row.full}`}
        className="flex w-full items-center gap-1.5 rounded-sm px-2 py-1 text-left font-mono-tight text-xs text-slate-600 hover:bg-slate-100"
        style={{ paddingLeft: 8 + row.depth * 14 }}>
        {row.open ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
        {row.open ? <FolderOpen className="h-3.5 w-3.5 text-amber-500" /> : <Folder className="h-3.5 w-3.5 text-amber-500" />}
        <span className="font-medium">{row.name}</span>
      </button>
    );
  }
  const Icon = iconFor(row.file.path);
  const isActive = current === row.file.path;
  return (
    <button type="button" onClick={() => onSelect(row.file.path)} data-testid={`file-${row.file.path}`}
      className={cn("flex w-full items-center gap-1.5 rounded-sm px-2 py-1 text-left font-mono-tight text-xs transition-colors", isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}
      style={{ paddingLeft: 8 + row.depth * 14 + 14 }}>
      <Icon className={cn("h-3.5 w-3.5 shrink-0", isActive ? "text-slate-300" : "text-slate-400")} />
      <span className="truncate">{row.file.path.split("/").pop()}</span>
    </button>
  );
}

function SearchBox({ query, onChange, onClear }) {
  return (
    <div className="relative mx-2 mb-2">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      <input
        value={query}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search code…"
        data-testid="code-search-input"
        className="h-8 w-full rounded-md border border-slate-200 bg-white pl-7 pr-7 font-mono-tight text-xs outline-none placeholder:text-slate-400 focus:border-slate-900"
      />
      {query && (
        <button type="button" onClick={onClear} aria-label="Clear search" data-testid="code-search-clear" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-900">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function FileTree({ files, root, rows, current, fit, search, onToggle, onSelect }) {
  const searching = search.query.trim().length >= 2;
  return (
    <div className={cn("border-b border-slate-200 bg-slate-50/70 py-2 md:border-b-0 md:border-r", fit ? "md:sticky md:top-4 md:max-h-[85vh] md:overflow-auto md:rounded-l-md" : "min-h-0 overflow-auto")} data-testid="file-tree">
      <SearchBox query={search.query} onChange={search.setQuery} onClear={search.clear} />
      {searching && <SearchResults hits={search.hits} query={search.query} onJump={search.jump} active={search.target} />}
      <p className="px-3 pb-1 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">{root || "project"} · {files.length} files</p>
      {rows.map((r) => <TreeRow key={r.kind === "dir" ? `d-${r.full}` : r.file.path} row={r} current={current} onToggle={onToggle} onSelect={onSelect} />)}
    </div>
  );
}

function CodeViewer({ root, current, lines, fit, search, viewerRef }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.warn("Clipboard unavailable", err);
      toast.error("Copy failed — clipboard access is blocked in this browser");
    }
  };
  const highlighting = search.query.trim().length >= 2;
  return (
    <div className={cn("flex min-w-0 flex-col bg-slate-950 text-slate-200", fit ? "min-h-[320px] rounded-r-md" : "min-h-0 h-full")}>
      <div className={cn("flex items-center justify-between border-b border-slate-800 px-4 py-2", fit && "sticky top-0 z-10 bg-slate-950")}>
        <span className="truncate font-mono-tight text-[11px] text-slate-400" data-testid="file-viewer-path">{root ? `${root}/` : ""}{current}</span>
        <div className="flex items-center gap-3">
          <span className="font-mono-tight text-[10px] text-slate-500">{lines.length} lines</span>
          <button type="button" onClick={copy} className="rounded p-1 text-slate-400 hover:text-white" aria-label="Copy file" data-testid="file-copy">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      <div ref={viewerRef} className={cn("min-h-0 flex-1", fit ? "overflow-x-auto" : "overflow-auto overscroll-contain")} data-testid="file-viewer">
        <table className="w-full border-collapse font-mono-tight text-[12px] leading-5">
          <tbody>
            {lines.map((l, i) => {
              const isTarget = search.target?.path === current && search.target.line === i + 1;
              return (
                <tr key={`${current}:${i + 1}`} data-line={i + 1} className={cn("hover:bg-white/5", isTarget && "bg-amber-400/15")} data-testid={isTarget ? "code-search-target-line" : undefined}>
                  <td className={cn("w-10 select-none border-r border-slate-800 pr-3 text-right", isTarget ? "text-amber-300" : "text-slate-600")}>{i + 1}</td>
                  <td className="whitespace-pre px-4 text-slate-200">{highlighting ? <Highlight text={l} query={search.query} /> : (l || " ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Code search state: query, hits, and the (path, line) target to scroll into view.
function useCodeSearch(files, current, select, viewerRef) {
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(null);
  const hits = useMemo(() => searchFiles(files, query), [files, query]);
  useEffect(() => {
    if (!target || target.path !== current) return;
    viewerRef.current?.querySelector(`[data-line="${target.line}"]`)?.scrollIntoView({ block: "center" });
  }, [target, current, viewerRef]);
  const jump = (h) => { setTarget(h); select(h.path); };
  const clear = () => { setQuery(""); setTarget(null); };
  return { query, setQuery, hits, target, jump, clear };
}

const defaultFile = (files) => files.find((f) => f.path === "main.tf" || f.path.endsWith("/main.tf"))?.path ?? files[0]?.path;

export function FileExplorer({ files = [], root = "", active, onSelect, height = null }) {
  const viewerRef = useRef(null);
  const [internal, setInternal] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const current = active ?? internal ?? defaultFile(files);
  const select = (p) => { setInternal(p); onSelect?.(p); };
  const toggleDir = (full) => setCollapsed((c) => { const n = new Set(c); n.has(full) ? n.delete(full) : n.add(full); return n; });
  const search = useCodeSearch(files, current, select, viewerRef);
  const rows = useMemo(() => flatten(buildTree(files), 0, "", collapsed, []), [files, collapsed]);
  const lines = (files.find((f) => f.path === current)?.content || "").split("\n");
  const fit = height === null;
  return (
    <div className={cn("grid rounded-md border border-slate-200 bg-white md:grid-cols-[240px_1fr]", fit ? "items-start" : "grid-rows-[minmax(0,1fr)] overflow-hidden")} style={fit ? undefined : { height }} data-testid="file-explorer">
      <FileTree files={files} root={root} rows={rows} current={current} fit={fit} search={search} onToggle={toggleDir} onSelect={select} />
      <CodeViewer root={root} current={current} lines={lines} fit={fit} search={search} viewerRef={viewerRef} />
    </div>
  );
}
